import type { LocationDto } from "../dtos/LocationDto.js";
import type { ProjectLocationTabSegmentCounts } from "../../domain/repositories/ILocationRepository.js";
import { getSupabaseClient } from "../../../../core/supabase/client.js";

const FIELDS = `id, name, code, description, categories, province_code, ward_code, address_line, full_address_cache, latitude, longitude, photos, metadata, created_at, updated_at, created_by`;
const LIST_FIELDS = `${FIELDS}, province:locations_provinces(code, name, full_name)`;
const DETAIL_FIELDS = `${FIELDS}, province:locations_provinces(code, name, full_name), ward:locations_wards(code, name, full_name)`;

export class LocationRemoteDataSource {
  private provincesPromise: Promise<{ code: string; name: string; full_name: string }[]> | null = null;

  private get supabase() {
    return getSupabaseClient() as any;
  }

  async list(
    options: {
      search?: string;
      limit?: number;
      offset?: number;
      orgId?: string;
      projectId?: string;
      scope?: "any" | "inProject" | "inOrg" | "global";
    } = {},
  ): Promise<{ data: LocationDto[]; total: number }> {
    if (options.orgId) {
      // Route through the org-scoped RPC whenever orgId is given, even with
      // no search text — the plain query below has no orgId filter at all
      // (locations aren't org-owned 1:1, only joined via org_locations), so
      // skipping this for an empty search silently returned unscoped rows.
      return this.searchEnriched({ ...options, search: options.search ?? "", orgId: options.orgId });
    }
    let query = this.supabase
      .from("locations")
      .select(LIST_FIELDS, { count: "exact" })
      .order("created_at", { ascending: false });
    if (
      typeof options.limit === "number" &&
      typeof options.offset === "number"
    ) {
      query = query.range(options.offset, options.offset + options.limit - 1);
    } else if (typeof options.limit === "number") {
      query = query.limit(options.limit);
    }
    const { data, error, count } = await query;
    if (error) throw new Error(error.message);
    return { data: data ?? [], total: count ?? 0 };
  }

  async listByIdsInProject(
    orgId: string,
    projectId: string,
    locationIds: string[],
  ): Promise<LocationDto[]> {
    if (!locationIds.length) return [];

    const { data, error } = await this.supabase
      .from("project_locations")
      .select(`location_id, location:locations!location_id(${LIST_FIELDS})`)
      .eq("org_id", orgId)
      .eq("project_id", projectId)
      .in("location_id", locationIds);
    if (error) throw new Error(error.message);

    return (data ?? [])
      .map((row: { location: LocationDto | null }) => row.location)
      .filter((location: LocationDto | null): location is LocationDto => location !== null);
  }

  async listProjectLocationTabPage(options: {
    orgId: string;
    projectId: string;
    segment: "all" | "sub" | "none";
    search?: string;
    provinceCodes?: string[];
    categoryNames?: string[];
    limit: number;
    offset: number;
  }): Promise<{
    data: LocationDto[];
    total: number;
    counts: { all: number; sub: number; none: number };
  }> {
    const [{ data, error }, provinces] = await Promise.all([
      this.supabase.rpc("list_project_location_tab_page", {
        p_org_id: options.orgId,
        p_project_id: options.projectId,
        p_segment: options.segment,
        search_query: options.search ?? "",
        p_province_codes: options.provinceCodes ?? [],
        p_category_names: options.categoryNames ?? [],
        row_limit: options.limit,
        row_offset: options.offset,
      }),
      this.listProvinces(),
    ]);
    if (error) throw new Error(error.message);

    const provinceByCode = new Map(provinces.map((p) => [p.code, p]));
    const rows = (data ?? []).map((r: LocationDto) => ({
      ...r,
      province: r.province_code ? (provinceByCode.get(r.province_code) ?? null) : null,
    }));
    return {
      data: rows,
      total: rows.length,
      counts: { all: 0, sub: 0, none: 0 },
    };
  }

  async countProjectLocationTabSegments(options: {
    orgId: string;
    projectId: string;
    search?: string;
    provinceCodes?: string[];
    categoryNames?: string[];
  }): Promise<ProjectLocationTabSegmentCounts> {
    const { data, error } = await this.supabase.rpc("count_project_location_tab_segments", {
      p_org_id: options.orgId,
      p_project_id: options.projectId,
      search_query: options.search ?? "",
      p_province_codes: options.provinceCodes ?? [],
      p_category_names: options.categoryNames ?? [],
    });
    if (error) throw new Error(error.message);
    const row = data?.[0] ?? {};
    return {
      all: Number(row.all_count ?? 0),
      sub: Number(row.sub_count ?? 0),
      none: Number(row.none_count ?? 0),
    };
  }

  async listProjectLocationTabCategories(options: {
    orgId: string;
    projectId: string;
    segment: "all" | "sub" | "none";
  }): Promise<string[]> {
    const { data, error } = await this.supabase.rpc("list_project_location_tab_categories", {
      p_org_id: options.orgId,
      p_project_id: options.projectId,
      p_segment: options.segment,
    });
    if (error) throw new Error(error.message);
    return (data ?? [])
      .map((row: { category: string | null }) => row.category?.trim() ?? "")
      .filter(Boolean);
  }

  // Full-text (pgroonga) search via search_locations_enriched — the RPC
  // returns bare `locations` rows, so we stitch in the province lookup
  // client-side to keep the shape mapDto() expects from the table-query path.
  private async searchEnriched(options: {
    search: string;
    limit?: number;
    offset?: number;
    orgId: string;
    projectId?: string;
    scope?: "any" | "inProject" | "inOrg" | "global";
  }): Promise<{ data: LocationDto[]; total: number }> {
    const [{ data, error }, provinces] = await Promise.all([
      this.supabase.rpc("search_locations_enriched", {
        p_org_id: options.orgId,
        p_project_id: options.projectId ?? null,
        search_query: options.search,
        row_limit: options.limit ?? 500,
        row_offset: options.offset ?? 0,
        p_scope: options.scope ?? "any",
      }),
      this.listProvinces(),
    ]);
    if (error) throw new Error(error.message);
    const provinceByCode = new Map(provinces.map((p) => [p.code, p]));
    const rows = (data ?? []).map((r: LocationDto) => ({
      ...r,
      province: r.province_code ? (provinceByCode.get(r.province_code) ?? null) : null,
    }));
    return { data: rows, total: rows.length };
  }

  async getById(id: string): Promise<LocationDto | null> {
    const { data, error } = await this.supabase
      .from("locations")
      .select(DETAIL_FIELDS)
      .eq("id", id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data ?? null;
  }

  async create(
    payload: Omit<
      LocationDto,
      "id" | "created_at" | "updated_at" | "created_by"
    >,
  ): Promise<LocationDto | null> {
    const {
      data: { user },
    } = await this.supabase.auth.getUser();
    const { data, error } = await this.supabase
      .from("locations")
      .insert({ ...payload, created_by: user?.id })
      .select(FIELDS)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data ?? null;
  }

  async update(
    id: string,
    payload: Partial<LocationDto>,
  ): Promise<LocationDto | null> {
    const { data, error } = await this.supabase
      .from("locations")
      .update(payload)
      .eq("id", id)
      .select(FIELDS)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data ?? null;
  }

  async delete(id: string): Promise<void> {
    const { error } = await this.supabase
      .from("locations")
      .delete()
      .eq("id", id);
    if (error) throw new Error(error.message);
  }

  async listByCodes(
    codes: string[],
  ): Promise<
    Pick<LocationDto, "id" | "name" | "code" | "full_address_cache">[]
  > {
    if (!codes.length) return [];
    const { data, error } = await this.supabase
      .from("locations")
      .select("id, name, code, full_address_cache")
      .in("code", codes);
    if (error) throw new Error(error.message);
    return data ?? [];
  }

  async searchLocationsByScope(options: {
    orgId: string;
    query: string;
    projectId?: string;
    limit?: number;
    offset?: number;
    scope?: string;
  }): Promise<any[]> {
    const { data, error } = await this.supabase.rpc("search_locations_by_scope", {
      p_org_id: options.orgId,
      p_project_id: options.projectId ?? null,
      search_query: options.query ?? "",
      row_limit: options.limit ?? 10,
      row_offset: options.offset ?? 0,
      p_scope: options.scope ?? "any",
    });
    if (error) throw new Error(error.message);
    return data ?? [];
  }

  async searchLocations(
    orgId: string,
    query: string,
    limit = 5,
  ): Promise<
    Pick<LocationDto, "id" | "name" | "code" | "full_address_cache">[]
  > {
    const rows = await this.searchLocationsByScope({
      orgId,
      query,
      limit,
      scope: "any",
    });
    return rows.map((r: any) => ({
      id: r.id,
      name: r.name,
      code: r.code,
      full_address_cache: r.full_address_cache,
    }));
  }

  async searchLocationsBatch(
    orgId: string,
    names: string[],
  ): Promise<Record<string, Pick<LocationDto, 'id' | 'name' | 'code' | 'full_address_cache'> | null>> {
    if (!names.length) return {};
    const { data, error } = await this.supabase.rpc('search_locations_batch', {
      p_org_id: orgId,
      p_names: names,
    });
    if (error) throw new Error(error.message);
    return (data ?? {}) as Record<string, Pick<LocationDto, 'id' | 'name' | 'code' | 'full_address_cache'> | null>;
  }

  async listProvinces(): Promise<{ code: string; name: string; full_name: string }[]> {
    if (!this.provincesPromise) {
      this.provincesPromise = this.supabase
        .from("locations_provinces")
        .select("code, name, full_name")
        .order("name")
        .then(({ data, error }: { data: { code: string; name: string; full_name: string }[] | null; error: { message: string } | null }) => {
          if (error) throw new Error(error.message);
          return data ?? [];
        })
        .catch((error: unknown) => {
          this.provincesPromise = null;
          throw error;
        });
    }
    return this.provincesPromise!;
  }

  async autocompleteAdminSuggestions(
    orgId: string,
    query: string,
  ): Promise<
    {
      displayName: string;
      provinceCode: string | null;
      wardCode: string | null;
      source: string;
    }[]
  > {
    const rows = await this.searchLocationsByScope({
      orgId,
      query,
      limit: 10,
      scope: "admin_geo",
    });
    return rows.map((r: any) => ({
      displayName: r.display_name,
      provinceCode: r.province_code ?? null,
      wardCode: r.ward_code ?? null,
      provinceName: r.province?.full_name ?? r.province?.name ?? null,
      wardName: r.ward?.full_name ?? r.ward?.name ?? null,
      source: r.source,
      locationId: r.location_id ?? r.id ?? null,
      locationName: r.name || null,
      locationCode: r.code || null,
      addressLine: r.address_line || null,
      fullAddressCache: r.full_address_cache || null,
    }));
  }
}
